import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/mocks/movies";

interface HeroCarouselProps {
  items: Movie[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (items.length <= 1) return;
    timer.current = window.setInterval(() => {
      setIndex((i) => (i + 1) % items.length);
    }, 6000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [items.length]);

  if (items.length === 0) {
    return (
      <div className="w-full h-[420px] md:h-[560px] bg-primary-900 flex items-center justify-center">
        <p className="text-background-300">精彩内容正在赶来，敬请期待</p>
      </div>
    );
  }

  const go = (i: number) => setIndex((i + items.length) % items.length);
  const active = items[index];

  return (
    <section className="relative w-full h-[440px] md:h-[600px] overflow-hidden bg-primary-950">
      {items.map((m, i) => (
        <div
          key={m.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === index ? "opacity-100" : "opacity-0"}`}
          aria-hidden={i !== index}
        >
          <img
            src={m.backdrop}
            alt={`${m.title} 剧照`}
            title={`${m.title} 影视内容 剧情介绍`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-transparent"></div>
        </div>
      ))}

      <div className="relative z-10 w-full h-full flex items-center px-4 md:px-8">
        <div key={active.id} className="max-w-2xl animate-fade-up">
          <div className="flex items-center gap-3 mb-4">
            <span className="px-2.5 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-bold">
              {active.type === "movie" ? "电影" : "电视剧"}
            </span>
            <span className="text-background-200 text-sm">{active.year} · {active.region}</span>
            <span className="text-accent-400 text-sm font-bold flex items-center gap-1">
              <i className="ri-fire-fill text-xs" aria-hidden="true"></i>
              {active.rating.toFixed(1)}
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 mb-4 tracking-wide">
            {active.title}
          </h1>
          <p className="text-sm md:text-base text-background-200 leading-relaxed mb-6 max-w-xl line-clamp-3">
            {active.summary}
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <Link
              to={`/detail/${active.id}`}
              className="px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-400 text-background-50 text-sm font-medium flex items-center gap-2 transition-colors whitespace-nowrap"
            >
              <i className="ri-play-circle-line" aria-hidden="true"></i>
              查看剧情介绍
            </Link>
            <Link
              to="/category"
              className="px-5 py-2.5 rounded-md bg-background-50/10 hover:bg-background-50/20 border border-background-50/30 text-background-50 text-sm font-medium transition-colors whitespace-nowrap"
            >
              浏览更多好片
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(index - 1)}
        className="hidden md:flex absolute left-5 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-background-50/15 hover:bg-background-50/30 text-background-50 backdrop-blur cursor-pointer transition-colors"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-2xl" aria-hidden="true"></i>
      </button>
      <button
        type="button"
        onClick={() => go(index + 1)}
        className="hidden md:flex absolute right-5 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-background-50/15 hover:bg-background-50/30 text-background-50 backdrop-blur cursor-pointer transition-colors"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-2xl" aria-hidden="true"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {items.map((m, i) => (
          <button
            key={m.id}
            type="button"
            onClick={() => go(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${i === index ? "w-6 bg-accent-500" : "w-1.5 bg-background-50/50"}`}
            aria-label={`切换到第${i + 1}张`}
          ></button>
        ))}
      </div>
    </section>
  );
}